/* eslint-disable class-methods-use-this */
const { PaymentStrategies, getShortageDamageUnits, Metrics } = require('pando-constants');
const { invert } = require('lodash');

const convert = require('convert-units');
const Helper = require('./utils/helper');

class Shortage {
  getNetToleranceByWeight(weight) {
    let currentWeightValue = weight.value;
    if (weight && weight.unit.toLowerCase() === Metrics.Kg) {
      currentWeightValue = convert(weight.value).from(Metrics.Kg)
        .to(getShortageDamageUnits().MT.toLowerCase());
    }
    return currentWeightValue;
  }
  getNetShortageCount(material, currentCategory) {
    let shortageCount = 0;
    if (material) {
      shortageCount = (material.epod && material.epod.shortage_count) || 0;
      if (currentCategory && currentCategory.shortage_tolerance) {
        let netTolerance = material.quantity ? material.quantity.value : 0;
        if (currentCategory.shortage_unit.toLowerCase() === getShortageDamageUnits().MT.toLowerCase()) {
          netTolerance = this.getNetToleranceByWeight(material.weight);
        }
        netTolerance *= 0.01 * (currentCategory.shortage_tolerance || 0);
        shortageCount = Math.max(shortageCount - netTolerance, 0);
      }
    }
    return shortageCount;
  }
  calculateChargeByCategory(material, category) {
    const type = category.shortage_type.toString();
    const count = this.getNetShortageCount(material, category);
    const shortageTypes = invert(PaymentStrategies.shortage);
    let result = 0;
    switch (type) {
      case shortageTypes.shortageUsingFixedConstant:
        return count * category.shortage_factor;
      case shortageTypes.shortageUsingFactorAndMrp:
        return count * category.shortage_factor * material.mrp;
      case shortageTypes.shortageUsingInvoice:
        if (category.shortage_unit
          && category.shortage_unit.toLowerCase() === getShortageDamageUnits().MT.toLowerCase()) {
          const materialWeight = this.getNetToleranceByWeight(material.weight);
          result = count * category.shortage_factor *
            (material.invoice.amount / materialWeight);
        } else {
          result = count * category.shortage_factor *
          (material.invoice.amount / material.quantity.value);
        }
        return result;
      default:
        logger.info('Unhandled shortage type');
        return 0;
    }
  }
  shortageUsingFixedConstant(data, clientConfiguration) {
    const computeSdcdAtCategoryLevel = Helper.getComputeSdcdAtCategoryLevel(clientConfiguration);
    // Check for seal no.
    if (Helper.isSealIntact(data.sealNoDetails)) {
      return { shortage: 0 };
    }
    const factor = data.defectsSettings.shortage.factor;
    const categoryMap = data.materials.category || {};
    const materialShortageDamageMap = {};
    const shortageCharge = data.destinations.reduce((acc, destination) =>
      acc + destination.paymentLevelMaterials.reduce((total, material) => {
        let totalCost = total;
        const locationId = destination.destinationGateId || destination.locationId;
        if (!materialShortageDamageMap[locationId]) {
          materialShortageDamageMap[locationId] = {};
        }
        const category = categoryMap[material.category];
        let calculatedCost = 0;
        if (category && category.shortage_type && computeSdcdAtCategoryLevel) {
          calculatedCost = this.calculateChargeByCategory(material, category);
        } else {
          calculatedCost = this.getNetShortageCount(material, category) * factor;
        }
        materialShortageDamageMap[locationId][material._id] = calculatedCost;
        totalCost += calculatedCost;
        return totalCost;
      }, 0), 0);
    return { shortage: shortageCharge.toFixed(2), materialShortageDamageMap };
  }
  shortageUsingFactorAndMrp(data, clientConfiguration) {
    const computeSdcdAtCategoryLevel = Helper.getComputeSdcdAtCategoryLevel(clientConfiguration);
    // Check for seal no.
    if (Helper.isSealIntact(data.sealNoDetails)) {
      return { shortage: 0 };
    }
    const categoryMap = data.materials.category || {};
    const factor = data.defectsSettings.shortage.factor;
    const materialShortageDamageMap = {};
    const shortageCharge = data.destinations.reduce((acc, destination) =>
      acc + destination.paymentLevelMaterials.reduce((total, material) => {
        let totalCost = total;
        const locationId = destination.destinationGateId || destination.locationId;
        if (!materialShortageDamageMap[locationId]) {
          materialShortageDamageMap[locationId] = {};
        }
        const category = categoryMap[material.category];
        let calculatedCost;
        if (category && category.shortage_type && computeSdcdAtCategoryLevel) {
          calculatedCost = this.calculateChargeByCategory(material, category);
        } else {
          calculatedCost = (this.getNetShortageCount(material, category) * factor * material.mrp);
        }
        materialShortageDamageMap[locationId][material._id] = calculatedCost;
        totalCost += calculatedCost;
        return totalCost;
      }, 0), 0);
    return { shortage: shortageCharge.toFixed(2), materialShortageDamageMap };
  }
  shortageUsingInvoice(data, clientConfiguration) {
    const computeSdcdAtCategoryLevel = Helper.getComputeSdcdAtCategoryLevel(clientConfiguration);
    // Check for seal no.
    if (Helper.isSealIntact(data.sealNoDetails)) {
      return { shortage: 0 };
    }
    const categoryMap = data.materials.category || {};
    const factor = data.defectsSettings.shortage.factor;
    const materialShortageDamageMap = {};
    const shortageCharge = data.destinations.reduce((acc, destination) =>
      acc + destination.paymentLevelMaterials.reduce((total, material) => {
        let totalCost = total;
        const locationId = destination.destinationGateId || destination.locationId;
        if (!materialShortageDamageMap[locationId]) {
          materialShortageDamageMap[locationId] = {};
        }
        const category = categoryMap[material.category];
        const count = this.getNetShortageCount(material, category);
        let calculatedCost;
        if (category && category.shortage_type && computeSdcdAtCategoryLevel) {
          calculatedCost = this.calculateChargeByCategory(material, category);
        } else if (category && category.shortage_unit &&
          category.shortage_unit.toLowerCase() === getShortageDamageUnits().MT.toLowerCase()) {
          const materialWeight = this.getNetToleranceByWeight(material.weight);
          calculatedCost = (count * factor *
          (material.invoice.amount / materialWeight));
        } else {
          calculatedCost = (count * factor *
          (material.invoice.amount / material.quantity.value));
        }
        materialShortageDamageMap[locationId][material._id] = calculatedCost;
        totalCost += calculatedCost;
        return totalCost;
      }, 0), 0);
    return { shortage: shortageCharge.toFixed(2), materialShortageDamageMap };
  }
  shortageUsingAmount(data) {
    // Check for seal no.
    if (Helper.isSealIntact(data.sealNoDetails)) {
      return { shortage: 0 };
    }
    const materialShortageDamageMap = {};
    const shortageCharge = data.destinations.reduce((acc, destination) =>
      acc + destination.paymentLevelMaterials.reduce((total, material) => {
        const locationId = destination.destinationGateId || destination.locationId;
        if (!materialShortageDamageMap[locationId]) {
          materialShortageDamageMap[locationId] = {};
        }
        const calculatedCost = (material.epod.shortage_charge || 0);
        materialShortageDamageMap[locationId][material._id] = calculatedCost;
        return total + calculatedCost;
      }, 0), 0);
    return { shortage: shortageCharge.toFixed(2), materialShortageDamageMap };
  }
  getTotalShortage(data) {
    const categoryMap = data.materials.category || {};
    const shortageCharge = data.destinations.reduce((acc, destination) =>
      acc + destination.paymentLevelMaterials.reduce((each, material) =>
        each +
        this.getNetShortageCount(material, categoryMap[material.category]), 0), 0);
    return shortageCharge;
  }
  getMaterialLevelShortage(data, clientConfiguration, materialShortageDamageMap = {}) {
    // Check for seal no.
    if (Helper.isSealIntact(data.sealNoDetails)) {
      return {};
    }
    return Helper.getMaterialLevelSdlc(data, clientConfiguration, 'shortage_charge', materialShortageDamageMap);
  }
  shortageUsingBaseFreight(data) {
    const freightCharge = data.overAllFreightRate;

    // Check for seal no.
    if (Helper.isSealIntact(data.sealNoDetails)) {
      return { shortage: 0 };
    }
    const categoryMap = data.materials.category || {};
    const factor = data.defectsSettings.shortage.factor;
    const tolerance = data.defectsSettings.shortage.tolerance;
    const defectUnit = data.defectsSettings.shortage.defectUnit;

    let totalMaterialCount = 0;
    const materialShortageDamageMap = {};
    const overAllShortage = data.allDestinations.reduce((acc, destination) =>
      acc + destination.materials.reduce((total, material) => {
        const currentCategory = categoryMap[material.category];
        if (defectUnit === getShortageDamageUnits().MT.toLowerCase()) {
          if (currentCategory &&
            currentCategory.shortage_unit &&
            currentCategory.shortage_unit.toLowerCase() === defectUnit) {
            totalMaterialCount += this.getNetToleranceByWeight(material.weight);
            total += (material.epod.shortage_count || 0);
          } else {
            // skip material
          }
        } else {
          totalMaterialCount += material.quantity.value;
          total += (material.epod.shortage_count || 0);
        }
        return total;
      }, 0), 0);
    const consigneeShortage = data.destinations.reduce((acc, destination) =>
      acc + destination.paymentLevelMaterials.reduce((total, material) => {
        const currentCategory = categoryMap[material.category];
        if (defectUnit === getShortageDamageUnits().MT.toLowerCase()) {
          if (currentCategory &&
            currentCategory.shortage_unit &&
            currentCategory.shortage_unit.toLowerCase() === defectUnit) {
            total += (material.epod.shortage_count || 0);
          } else {
            // skip material
          }
        } else {
          total += (material.epod.shortage_count || 0);
        }
        return total;
      }, 0), 0);

    const shortagePercent = (overAllShortage * 100) / totalMaterialCount;
    const netShortagePercent = Math.max(shortagePercent - tolerance, 0);
    const netShortage = ((netShortagePercent * consigneeShortage) / overAllShortage) / 100;
    const shortageCharge = netShortage * factor * freightCharge;
    const calculatedCost = (Math.round((shortageCharge + Number.EPSILON) * 100) / 100);
    data.destinations.forEach((destination) => {
      destination.paymentLevelMaterials.forEach((material) => {
        const locationId = destination.destinationGateId || destination.locationId;
        if (!materialShortageDamageMap[locationId]) {
          materialShortageDamageMap[locationId] = {};
        }
        const cost =
          ((material.epod.shortage_count || 0) / consigneeShortage) * calculatedCost;
        materialShortageDamageMap[locationId][material._id] = cost;
      });
    });
    return { shortage: calculatedCost, materialShortageDamageMap };
    /*
      round to 2 decimal place

      reason to add EPSILON
      no EPSILON - 1.005 => 1
      with EPSILON - 1.005 => 1.01
    */
  }
}

module.exports = Shortage;
